import React, { useState, useEffect, useRef } from 'react';
import { Send, Sparkles, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

import { API, AUTH_URL } from "../config";

const AIAssistant = () => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  const suggestedPrompts = [
    'What is the best direction for a kitchen?',
    'How can I improve energy in my home office?',
    'What remedies can I use for South-West entrance?',
    'Explain the significance of Northeast zone',
  ];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async (messageText = null) => {
    const text = messageText || input;
    if (!text.trim() || !user) return;

    const userMessage = { role: 'user', content: text, timestamp: new Date() };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/ai-assistant/chat`,
        {
          message: text,
          session_id: sessionId,
        },
        { withCredentials: true }
      );

      if (!sessionId) {
        setSessionId(response.data.session_id);
      }

      const assistantMessage = {
        role: 'assistant',
        content: response.data.response,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage = {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="main-content min-h-screen flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <Sparkles className="w-16 h-16 mx-auto mb-4 text-[#232323]" />
          <h2 className="text-3xl font-bold mb-4 text-[#232323]">AI Vastu Assistant</h2>
          <p className="text-[#353535] mb-6">
            Please log in to chat with our AI Vastu expert.
          </p>
          <a
            href={AUTH_URL ? `${AUTH_URL}/?redirect=${encodeURIComponent(window.location.href)}` : '/login'}
            className="btn-primary"
          >
            Login with Google
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content min-h-screen py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-[var(--accent-purple-200)] px-4 py-2 rounded-full mb-4">
            <Sparkles className="w-5 h-5 text-[#232323]" />
            <span className="font-semibold text-[#232323]">AI Vastu Expert</span>
          </div>
          <h1 className="text-3xl font-bold text-[#232323] mb-2">Ask Anything About Vastu</h1>
          <p className="text-[#353535]">Get instant answers from our AI-powered Vastu consultant</p>
        </div>

        {/* Chat Container */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden flex flex-col h-[600px]">
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            {messages.length === 0 ? (
              <div className="text-center py-12">
                <Sparkles className="w-12 h-12 mx-auto mb-4 text-[#999999]" />
                <p className="text-[#353535] mb-6">Start a conversation with our AI assistant</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-2xl mx-auto">
                  {suggestedPrompts.map((prompt, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleSend(prompt)}
                      className="text-left p-4 bg-[var(--accent-blue-200)] rounded-xl hover-lift text-sm"
                    >
                      {prompt}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              messages.map((message, idx) => (
                <div
                  key={idx}
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] p-4 rounded-2xl ${
                      message.role === 'user'
                        ? 'bg-[#232323] text-white'
                        : 'bg-[var(--accent-purple-200)] text-[#232323]'
                    }`}
                  >
                    <p className="text-sm leading-relaxed whitespace-pre-wrap">{message.content}</p>
                  </div>
                </div>
              ))
            )}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-[var(--accent-purple-200)] p-4 rounded-2xl">
                  <Loader2 className="w-5 h-5 animate-spin text-[#232323]" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="border-t border-gray-200 p-4">
            <div className="flex gap-3">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                placeholder="Ask about Vastu principles, remedies, or directions..."
                className="flex-1 px-4 py-3 border-2 border-[#999999] rounded-full focus:border-[#232323] focus:outline-none"
              />
              <button
                onClick={() => handleSend()}
                disabled={!input.trim() || loading}
                className="btn-primary !rounded-full w-12 h-12 p-0 flex items-center justify-center"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
            <p className="text-xs text-[#999999] mt-2 text-center">
              AI responses are for guidance only. Consult with certified experts for major decisions.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIAssistant;
